// Account screen — driven entirely by live data.
//
// Sources:
//   - auth store: name (from JWT-bound user record).
//   - sessions list (`GET /api/v1/sessions`): chat history with topic + size.
//   - active session profile (Zustand, hydrated by /sessions/active on Chat
//     mount): age/sex/goals/meds, intake_complete flag.
//
// Mock data has been deleted; sections that require flows we haven't built yet
// (subscriptions, GDPR export, payment) render as empty states or stubs that
// don't pretend to be real.

import { useMemo } from "react";

import {
  Button,
  IconButton,
  Surface,
  TopBar,
  Wordmark,
} from "@/design-system";
import { useQueryClient } from "@tanstack/react-query";

import {
  qk,
  useCreateSessionMutation,
  useProfileSummaryQuery,
  useResumeSessionMutation,
  useSessionsListQuery,
} from "@/api/queries";
import { useBranding } from "@/lib/branding";
import { useInstallPWA } from "@/lib/use-pwa-install";
import { formatDateLong, formatDateShort } from "@/lib/format";
import { goalLabel } from "@/lib/labels";
import { useScreenTitle } from "@/lib/use-screen-title";
import { cn } from "@/lib/utils";
import { useAuth } from "@/stores/auth";
import { useSession } from "@/stores/session";

export interface AccountProps {
  onBack: () => void;
  onSeeRecommendation: () => void;
  onStartChat: () => void;
}

const SETTINGS = [
  "Privacy e dati",
  "Esporta i miei dati (GDPR)",
  "Cancella il mio account",
  "Metodo di pagamento",
  "Indirizzo di spedizione",
] as const;
const DANGER_INDEX = 2;

function joinGoals(goals: unknown): string {
  if (!Array.isArray(goals) || goals.length === 0) return "—";
  return goals
    .filter((g): g is string => typeof g === "string")
    .map(goalLabel)
    .join(", ");
}

function joinMeds(meds: unknown): string {
  if (!Array.isArray(meds) || meds.length === 0) return "Nessuno dichiarato";
  return meds
    .filter((m): m is string => typeof m === "string")
    .join(", ");
}

function snippet(text: string | null | undefined, maxLen = 60): string | null {
  if (!text) return null;
  const t = text.trim().replace(/\s+/g, " ");
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen - 1)}…`;
}

export function Account({ onBack, onSeeRecommendation, onStartChat }: AccountProps) {
  useScreenTitle("Il mio account");
  const { legalFooterText, displayName } = useBranding();
  const profile = useSession((s) => s.profile);
  const intakeComplete = useSession((s) => s.intakeComplete);
  const sessionReset = useSession((s) => s.reset);
  const sessionSetId = useSession((s) => s.setSessionId);
  const resumeSession = useResumeSessionMutation();
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const sessionsQuery = useSessionsListQuery();
  const profileSummaryQuery = useProfileSummaryQuery();
  const createSession = useCreateSessionMutation();
  const queryClient = useQueryClient();
  const { canInstall, triggerInstall } = useInstallPWA();

  /**
   * "Aggiorna con MIMIR" must produce a FRESH chat session, not resume the
   * existing one (whose intake is already complete). We:
   *   1. wipe Zustand so Chat won't render stale thread on mount,
   *   2. invalidate the cached /sessions/active result so the resume logic
   *      doesn't re-hydrate the completed session,
   *   3. create a new backend session (this also seeds the Zustand thread
   *      with MIMIR's greeting via the mutation's onSuccess),
   *   4. navigate to /chat.
   */
  function startNewConversation() {
    sessionReset();
    void queryClient.invalidateQueries({ queryKey: qk.activeSession });
    createSession.mutate(undefined, {
      onSuccess: () => {
        onStartChat();
      },
    });
  }

  // Display the most authoritative known profile: the active session's
  // profile if intake is complete in this session, otherwise the consolidated
  // user_profiles memory built from ALL completed sessions. This way "Età",
  // "Obiettivi", "Farmaci" stay populated when the user opens a fresh chat
  // (which doesn't yet have its own profile_json).
  const consolidated = profileSummaryQuery.data?.structured_facts ?? null;
  const displayProfile: Record<string, unknown> | null =
    profile && typeof profile["age"] === "number"
      ? (profile as Record<string, unknown>)
      : consolidated && typeof consolidated["age"] === "number"
        ? consolidated
        : null;

  const ageStr =
    displayProfile && typeof displayProfile["age"] === "number"
      ? String(displayProfile["age"])
      : "—";
  const goalsStr = displayProfile ? joinGoals(displayProfile["goals"]) : "—";
  const medsStr = displayProfile
    ? joinMeds(displayProfile["medications_text"])
    : "Nessuno dichiarato";

  const lastActivityIso = useMemo(() => {
    const sessions = sessionsQuery.data ?? [];
    return sessions[0]?.last_activity_at ?? null;
  }, [sessionsQuery.data]);
  const updatedStr = lastActivityIso
    ? formatDateLong(new Date(lastActivityIso))
    : "—";

  function handleLogout() {
    sessionReset();
    logout();
  }

  return (
    <Surface>
      <TopBar
        left={
          <div className="flex items-center gap-2.5">
            <IconButton ariaLabel="Indietro" onClick={onBack}>
              <svg
                width="10"
                height="16"
                viewBox="0 0 10 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              >
                <path d="M8 2L2 8l6 6" />
              </svg>
            </IconButton>
            <Wordmark size={15} displayName={displayName} />
          </div>
        }
        right={
          <button
            type="button"
            onClick={handleLogout}
            className="font-sans text-[13px] text-ink-muted hover:text-ink bg-transparent border-none cursor-pointer px-2 py-1"
          >
            Esci
          </button>
        }
      />

      {/* ─── Header ──────────────────────────────────────── */}
      <header className="px-6 pt-6 pb-2">
        <div className="font-mono text-[11px] uppercase tracking-[1.2px] text-ink-faint mb-1.5">
          Buonasera
        </div>
        <h1 className="font-serif text-[32px] m-0 font-medium tracking-[-0.4px]">
          {user?.first_name ?? "Ospite"}
        </h1>
      </header>

      {/* ─── Active plan ─────────────────────────────────── */}
      <section className="px-5 pt-5 pb-2">
        <ASectionTitle>Il mio piano</ASectionTitle>
        <div className="bg-paper-card rounded-card p-5 shadow-card">
          {intakeComplete ? (
            <div className="text-[14px] leading-[1.5] text-ink-muted">
              <p className="m-0 mb-3">
                La tua raccomandazione è pronta. Aprila per vedere il piano nel
                dettaglio (principi attivi, dosi, evidenza).
              </p>
              <Button variant="primary" onClick={onSeeRecommendation}>
                Vedi la raccomandazione
              </Button>
            </div>
          ) : (
            <div className="text-[14px] leading-[1.5] text-ink-muted">
              <p className="m-0 mb-3">
                Non hai ancora un piano. Completa la conversazione con MIMIR
                per costruire la tua raccomandazione.
              </p>
              <Button
                variant="primary"
                onClick={startNewConversation}
                disabled={createSession.isPending}
              >
                {createSession.isPending ? "Avvio…" : "Inizia con MIMIR"}
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* ─── Profile ─────────────────────────────────────── */}
      <section className="px-5 pt-6 pb-2">
        <ASectionTitle>Il mio profilo</ASectionTitle>
        <div className="bg-paper-card rounded-card p-5 shadow-card">
          <ProfileRow k="Nome" v={user ? `${user.first_name} ${user.last_name}` : "—"} />
          <ProfileRow k="Email" v={user?.email ?? "—"} />
          <ProfileRow k="Età" v={ageStr} />
          <ProfileRow k="Obiettivi" v={goalsStr} />
          <ProfileRow k="Farmaci" v={medsStr} />
          <ProfileRow k="Aggiornato" v={updatedStr} last />

          <div
            className="mt-3.5 p-3.5 rounded-[10px]"
            style={{
              background: "rgba(184,98,58,0.06)",
              border: "0.5px solid rgba(184,98,58,0.18)",
            }}
          >
            <div className="font-serif text-[15.5px] italic text-ink mb-2 leading-[1.45]">
              Vuoi raccontare a MIMIR un cambiamento? Inizia una nuova
              conversazione — il piano si aggiornerà di conseguenza.
            </div>
            <Button
              variant="ghost"
              className="!h-10 !px-4 !text-sm"
              onClick={startNewConversation}
              disabled={createSession.isPending}
            >
              {createSession.isPending ? "Avvio…" : "Aggiorna con MIMIR →"}
            </Button>
          </div>
        </div>
      </section>

      {/* ─── History ─────────────────────────────────────── */}
      <section className="px-5 pt-6 pb-2">
        <ASectionTitle>Storico conversazioni</ASectionTitle>
        <div className="bg-paper-card rounded-card shadow-card overflow-hidden">
          {sessionsQuery.isPending && (
            <div className="px-5 py-6 text-center text-[13px] text-ink-faint font-mono uppercase tracking-[0.6px]">
              caricamento…
            </div>
          )}
          {sessionsQuery.isError && (
            <div className="px-5 py-6 text-center text-[13px] text-brick-danger">
              Non riesco a recuperare lo storico.
            </div>
          )}
          {sessionsQuery.data && sessionsQuery.data.length === 0 && (
            <div className="px-5 py-6 text-center text-[13px] text-ink-muted">
              Nessuna conversazione ancora.
            </div>
          )}
          {sessionsQuery.data?.map((row, i, arr) => {
            const topic = snippet(row.first_user_message) ?? "Conversazione";
            const status = row.intake_complete ? "Completata" : "In corso";
            const handleOpen = () => {
              if (row.intake_complete) {
                sessionSetId(row.session_id);
                onSeeRecommendation();
                return;
              }
              // Resume the *picked* in-progress session by hydrating its
              // thread from the backend, then navigate. This avoids the
              // "user clicks an old session and lands on the most-recent
              // one instead" UX bug.
              resumeSession.mutate(row.session_id, {
                onSuccess: () => onStartChat(),
              });
            };
            return (
              <button
                type="button"
                key={row.session_id}
                onClick={handleOpen}
                className={cn(
                  "w-full text-left px-5 py-4 flex justify-between items-center gap-3",
                  "hover:bg-[rgba(60,50,40,0.03)] focus-visible:bg-[rgba(60,50,40,0.04)]",
                  "focus-visible:outline-none transition-colors",
                  i < arr.length - 1 && "border-b border-[rgba(60,50,40,0.08)]",
                )}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-[16px] font-medium truncate">
                    {topic}
                  </div>
                  <div className="text-[12.5px] text-ink-muted mt-0.5">
                    {status} · {row.message_count} messagg{row.message_count === 1 ? "io" : "i"}
                  </div>
                </div>
                <div className="text-right shrink-0 flex items-center gap-2">
                  <div className="font-mono text-[11px] text-ink-faint">
                    {formatDateShort(new Date(row.last_activity_at))}
                  </div>
                  <svg
                    width="6"
                    height="10"
                    viewBox="0 0 6 10"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    className="text-ink-faint"
                    aria-hidden="true"
                  >
                    <path d="M1 1l4 4-4 4" />
                  </svg>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* ─── App install ─────────────────────────────────── */}
      {canInstall && (
        <section className="px-5 pt-6 pb-2">
          <ASectionTitle>App</ASectionTitle>
          <div className="bg-paper-card rounded-card p-5 shadow-card">
            <p className="m-0 mb-3 text-[14px] leading-[1.5] text-ink-muted">
              Installa InCorpoSano sul tuo dispositivo per accedervi rapidamente.
            </p>
            <Button variant="primary" onClick={() => void triggerInstall()}>
              Installa l'app
            </Button>
          </div>
        </section>
      )}

      {/* ─── Settings ────────────────────────────────────── */}
      <section className="px-5 pt-6 pb-7">
        <ASectionTitle>Impostazioni</ASectionTitle>
        <div className="bg-paper-card rounded-card shadow-card overflow-hidden">
          {SETTINGS.map((label, i, arr) => (
            <div
              key={label}
              className={cn(
                "px-5 py-4 flex justify-between items-center font-serif text-[16px] font-medium",
                i === DANGER_INDEX ? "text-brick-danger" : "text-ink",
                i < arr.length - 1 && "border-b border-[rgba(60,50,40,0.08)]",
              )}
            >
              <span>{label}</span>
              <svg
                width="6"
                height="10"
                viewBox="0 0 6 10"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                className="text-ink-faint"
                aria-hidden="true"
              >
                <path d="M1 1l4 4-4 4" />
              </svg>
            </div>
          ))}
        </div>

        <div className="text-center mt-6 text-[11.5px] text-ink-faint leading-[1.5]">
          {legalFooterText}
          <br />
          Versione 1.0 · <span className="font-serif italic">in corpore sano</span>
        </div>
      </section>
    </Surface>
  );
}

// ─── Sub-components ──────────────────────────────────────────

function ASectionTitle({ children }: { children: string }) {
  return (
    <h2 className="font-mono text-[11px] uppercase tracking-[1.2px] text-ink-faint mt-0 mb-2.5 ml-1 font-medium">
      {children}
    </h2>
  );
}

function ProfileRow({
  k,
  v,
  last = false,
}: {
  k: string;
  v: string;
  last?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex justify-between items-center gap-4 py-3 text-[14px]",
        !last && "border-b border-[rgba(60,50,40,0.08)]",
      )}
    >
      <span className="text-ink-muted shrink-0">{k}</span>
      <span className="text-ink text-right break-words">{v}</span>
    </div>
  );
}
